import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "MDP UAT Review";

export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      logger.debug("[login] success → redirecting to /home", result);
      navigate("/home");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="from-primary/8 via-background to-background flex min-h-screen items-center justify-center bg-linear-to-br px-4">
      <Card className="border-t-primary w-full max-w-md border-t-4 shadow-lg">
        <CardHeader className="space-y-3 text-center">
          <BrandLogo name={PROJECT_NAME} className="mx-auto h-12 text-2xl" />
          <div className="space-y-1">
            <p className="text-primary text-xs font-semibold tracking-[0.22em] uppercase">
              gategroup
            </p>
            <p className="text-muted text-sm">Sign in to the MDP UAT review workspace</p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="name@gategroup.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-foreground text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <label htmlFor="rememberMe" className="text-foreground text-sm">
                Keep me signed in
              </label>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={loginMutation.isPending}
              className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Access workspace"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
